import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { type Deal, type CamStructure } from "../api/client";
import EmptyState from "./EmptyState";
import { Upload, Settings, Save, Check, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import { fmtSF, fmtPercent, fmtPSF, fmtCurrency } from "../utils/format";
import { prettyFieldLabel } from "../utils/labels";
import LeaseAnalysisUpload from "./LeaseAnalysisUpload";

/* -------------------------------------------------------------------------- */
/* Shared types                                                               */
/* -------------------------------------------------------------------------- */

export interface PoolReimbursement {
  pool_id: string;
  reimbursement_type: string;
  base_year?: number | null;
  base_year_amount?: number | null;
  expense_cap_pct?: number | null;
  admin_fee_pct?: number | null;
}

export interface TenantSummary {
  tenant_id: string;
  tenant_name: string;
  suite_id: string | null;
  leased_sf: number;
  lease_type: string;
  cam_structure: CamStructure;
  pro_rata_share?: number | null;
  pool_reimbursements?: PoolReimbursement[];
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

const CAM_METHODS = [
  { value: "gross", label: "None (Gross)" },
  { value: "NNN", label: "NNN (Pro-Rata Share)" },
  { value: "base_year_stop", label: "Base Year Stop" },
  { value: "expense_stop", label: "Expense Stop ($/SF)" },
  { value: "expense_cap", label: "Expense Cap" },
  { value: "fixed_psf", label: "Fixed $/SF" },
];

const TAX_INS_METHODS = [
  { value: "none", label: "None" },
  { value: "NNN", label: "NNN (Pro-Rata)" },
  { value: "base_year_stop", label: "Base Year Stop" },
  { value: "expense_cap", label: "Expense Cap" },
];

const EXCLUSION_OPTIONS = [
  { value: "roof", label: "Roof" },
  { value: "structural", label: "Structural" },
  { value: "capital", label: "Capital" },
  { value: "management", label: "Management" },
  { value: "anchor_specific", label: "Anchor-Specific" },
];

/* -------------------------------------------------------------------------- */
/* Shared helpers (exported for use in ReimbursementsPage)                     */
/* -------------------------------------------------------------------------- */

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="text-[11px] font-medium text-bark-500 block mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * A percent input that uses local string state to avoid cursor-jump
 * when the parent reformats on every render.
 */
export function PctInput({
  value,
  onChange,
  placeholder,
  step = "0.1",
}: {
  value: number | null | undefined;
  onChange: (decimal: number | null) => void;
  placeholder?: string;
  step?: string;
}) {
  const [local, setLocal] = useState(() =>
    value != null ? (value * 100).toFixed(2) : ""
  );
  // Sync from prop when it changes externally (e.g. reset)
  useEffect(() => {
    setLocal(value != null ? (value * 100).toFixed(2) : "");
  }, [value]);

  return (
    <input
      type="number"
      step={step}
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => {
        if (local === "" || local == null) {
          onChange(null);
        } else {
          onChange(parseFloat(local) / 100);
        }
      }}
      placeholder={placeholder}
      className="input-sm"
    />
  );
}

const CAM_BADGE_COLORS: Record<string, string> = {
  nnn: "bg-forest-50 text-forest-700 border-forest-200",
  gross: "bg-honey-50 text-honey-700 border-honey-200",
  base_year_stop: "bg-blue-50 text-blue-700 border-blue-200",
  expense_cap: "bg-blue-50 text-blue-700 border-blue-200",
  fixed_psf: "bg-forest-50 text-forest-700 border-forest-200",
  modified_gross: "bg-honey-50 text-honey-700 border-honey-200",
  pending: "bg-grove-200 text-bark-500 border-grove-300",
  "--": "bg-grove-200 text-bark-500 border-grove-300",
};

const CAM_BADGE_LABELS: Record<string, string> = {
  nnn: "NNN",
  gross: "Gross",
  base_year_stop: "Base Yr Stop",
  expense_cap: "Expense Cap",
  fixed_psf: "Fixed $/SF",
  modified_gross: "Mod. Gross",
  pending: "Pending",
  excluded: "Excluded",
  "--": "Pending",
};

export function CamBadge({ type }: { type: string }) {
  const lower = (type || "--").toLowerCase();
  return (
    <span
      className={`text-[10px] px-2 py-0.5 rounded-md border font-medium whitespace-nowrap ${
        CAM_BADGE_COLORS[lower] ||
        "bg-grove-100 text-bark-500 border-grove-200"
      }`}
    >
      {CAM_BADGE_LABELS[lower] || type}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Category Caps                                                              */
/* -------------------------------------------------------------------------- */

const CATEGORY_OPTIONS = [
  { value: "utilities", label: "Utilities" },
  { value: "repairs_and_maintenance", label: "Repairs & Maintenance" },
  { value: "janitorial", label: "Janitorial" },
  { value: "landscaping", label: "Landscaping" },
  { value: "administrative", label: "Administrative" },
  { value: "insurance", label: "Insurance" },
  { value: "real_estate_taxes", label: "Real Estate Taxes" },
  { value: "management_fee", label: "Management Fee" },
];

interface CategoryCap {
  category: string;
  cap_pct: number;
  compounding: boolean;
}

function CategoryCapsSection({
  cam,
  tenantId,
  updateEdit,
  edits,
}: {
  cam: CamStructure;
  tenantId: string;
  updateEdit: (tenantId: string, field: string, value: unknown) => void;
  edits: Record<string, unknown>;
}) {
  const [open, setOpen] = useState(false);
  const caps: CategoryCap[] = (cam as unknown as Record<string, unknown>).category_caps as CategoryCap[] ?? [];

  const updateCaps = (newCaps: CategoryCap[]) => {
    updateEdit(tenantId, "category_caps", newCaps);
  };

  const addCap = () => {
    const used = new Set(caps.map((c) => c.category));
    const available = CATEGORY_OPTIONS.find((o) => !used.has(o.value));
    if (!available) return;
    updateCaps([...caps, { category: available.value, cap_pct: 0.05, compounding: true }]);
    setOpen(true);
  };

  const removeCap = (idx: number) => {
    updateCaps(caps.filter((_, i) => i !== idx));
  };

  const updateCapField = (idx: number, field: string, value: unknown) => {
    const updated = caps.map((c, i) => (i === idx ? { ...c, [field]: value } : c));
    updateCaps(updated);
  };

  return (
    <div className="mt-4 bg-white rounded-lg border border-grove-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-grove-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-bark-400" /> : <ChevronRight className="w-3.5 h-3.5 text-bark-400" />}
          <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500">
            Per-Category Caps
          </h4>
          {caps.length > 0 && (
            <span className="text-[10px] bg-forest-50 text-forest-700 px-1.5 py-0.5 rounded-full font-medium">
              {caps.length}
            </span>
          )}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-grove-200">
          {caps.length > 0 && (
            <table className="w-full text-xs mt-3">
              <thead>
                <tr className="border-b border-grove-200">
                  <th className="text-left py-1.5 text-bark-400 font-medium">Category</th>
                  <th className="text-left py-1.5 text-bark-400 font-medium w-24">Cap %</th>
                  <th className="text-center py-1.5 text-bark-400 font-medium w-24">Compounding</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {caps.map((cap, idx) => (
                  <tr key={idx} className="border-b border-grove-100">
                    <td className="py-1.5">
                      <select
                        value={cap.category}
                        onChange={(e) => updateCapField(idx, "category", e.target.value)}
                        className="input-sm text-xs w-full"
                      >
                        {CATEGORY_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="py-1.5 px-1">
                      <PctInput
                        value={cap.cap_pct}
                        onChange={(v) => updateCapField(idx, "cap_pct", v ?? 0)}
                        placeholder="e.g. 5.0"
                      />
                    </td>
                    <td className="py-1.5 text-center">
                      <input
                        type="checkbox"
                        checked={cap.compounding}
                        onChange={(e) => updateCapField(idx, "compounding", e.target.checked)}
                        className="rounded border-grove-300"
                      />
                    </td>
                    <td className="py-1.5 text-center">
                      <button
                        onClick={() => removeCap(idx)}
                        className="text-red-400 hover:text-red-600 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <button
            onClick={addCap}
            disabled={caps.length >= CATEGORY_OPTIONS.length}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-forest-700 bg-forest-50 border border-forest-200 rounded-lg hover:bg-forest-100 disabled:opacity-40 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add Category Cap
          </button>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* DefinePanel — ARGUS-style per-tenant reimbursement configuration           */
/* -------------------------------------------------------------------------- */

interface DefinePanelProps {
  tenants: TenantSummary[];
  expandedId: string | null;
  onToggle: (id: string) => void;
  getEditedCam: (t: TenantSummary) => CamStructure;
  getEditedLeaseType: (t: TenantSummary) => string;
  updateEdit: (tenantId: string, field: string, value: unknown) => void;
  setCamMethod: (tenantId: string, camType: string) => void;
  saveTenant: (tenantId: string) => void;
  saving: string | null;
  saved: Set<string>;
  edits: Record<string, unknown>;
  dealId: string;
  deal: Deal;
  // C3: Bulk selection (optional for backwards compat)
  bulkSelectedIds?: Set<string>;
  onBulkToggleId?: (id: string) => void;
}

export default function ReimbursementEditor({
  tenants,
  expandedId,
  onToggle,
  getEditedCam,
  getEditedLeaseType,
  updateEdit,
  setCamMethod,
  saveTenant,
  saving,
  saved,
  edits,
  dealId,
  deal,
  bulkSelectedIds,
  onBulkToggleId,
}: DefinePanelProps) {
  const totalLeasableArea = deal.property_data?.total_leasable_area || 0;
  const propertyPools = deal.property_data?.expense_pool_definitions || [];
  if (tenants.length === 0) {
    return (
      <EmptyState
        icon={Settings}
        title="No tenants to configure"
        description="Upload and parse a rent roll to define reimbursement structures."
        action={
          <Link
            to={`/deals/${dealId}/documents`}
            className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all duration-200 shadow-md"
          >
            <Upload className="w-4 h-4" />
            Upload Rent Roll
          </Link>
        }
      />
    );
  }

  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      <div className="px-6 py-5 border-b border-grove-200">
        <h3 className="font-display text-sm font-semibold text-bark-700">
          Tenant Recovery Configuration
        </h3>
        <p className="text-xs text-bark-300 mt-1">
          Click a tenant to expand and configure CAM, tax, and insurance
          recovery methods, caps, and stops per pool.
        </p>
      </div>
      <div className="divide-y divide-grove-200">
        {tenants.map((t, tenantIdx) => {
          const isExpanded = expandedId === t.tenant_id;
          const cam = getEditedCam(t);
          const lt = getEditedLeaseType(t);
          const isDirty = !!edits[t.tenant_id as keyof typeof edits];
          const isSaved = saved.has(t.tenant_id);
          const isSaving = saving === t.tenant_id;

          return (
            <div key={t.tenant_id}>
              {/* Tenant header row */}
              <button
                onClick={() => onToggle(t.tenant_id)}
                className="w-full text-left px-6 py-3 flex items-center gap-4 hover:bg-grove-50 transition-colors"
              >
                {/* C3: Bulk selection checkbox */}
                {bulkSelectedIds && onBulkToggleId && (
                  <input
                    type="checkbox"
                    checked={bulkSelectedIds.has(t.tenant_id)}
                    onChange={(e) => { e.stopPropagation(); onBulkToggleId(t.tenant_id); }}
                    onClick={(e) => e.stopPropagation()}
                    className="rounded border-grove-300 text-forest-600 focus:ring-forest-500"
                  />
                )}
                <span
                  className={`text-[10px] text-bark-300 transition-transform duration-200 ${
                    isExpanded ? "rotate-90" : ""
                  }`}
                >
                  &#9656;
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-bark-700 truncate">
                      {t.tenant_name}
                    </span>
                    {t.suite_id && (
                      <span className="text-xs text-bark-400">
                        Suite {t.suite_id}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-bark-400 mt-0.5">
                    {fmtSF(t.leased_sf)}
                  </div>
                </div>
                <CamBadge type={cam.cam_type || lt} />
                {isDirty && !isSaved && (
                  <span className="text-[10px] text-honey-600 font-medium">
                    unsaved
                  </span>
                )}
                {isSaved && (
                  <span className="text-[10px] text-forest-600 font-medium flex items-center gap-0.5">
                    <Check className="w-3 h-3" /> saved
                  </span>
                )}
              </button>

              {/* Expanded editor */}
              {isExpanded && (
                <div className="px-6 pb-5 pt-2 bg-grove-50 border-t border-grove-200">
                  {/* AI Lease Analysis */}
                  <div className="mb-4">
                    <LeaseAnalysisUpload
                      dealId={dealId}
                      tenantIdx={tenantIdx}
                      tenantName={t.tenant_name}
                      onApplied={(update) => {
                        // Apply directly: set lease type and all cam_structure fields
                        updateEdit(t.tenant_id, "lease_type", update.lease_type);
                        if (update.cam_structure) {
                          for (const [key, val] of Object.entries(update.cam_structure)) {
                            updateEdit(t.tenant_id, key, val);
                          }
                        }
                        if (update.pro_rata_share != null) {
                          updateEdit(t.tenant_id, "pro_rata_share", update.pro_rata_share);
                        }
                      }}
                      onEditApply={(update) => {
                        // Same as apply — user can then edit in the fields below
                        updateEdit(t.tenant_id, "lease_type", update.lease_type);
                        if (update.cam_structure) {
                          for (const [key, val] of Object.entries(update.cam_structure)) {
                            updateEdit(t.tenant_id, key, val);
                          }
                        }
                        if (update.pro_rata_share != null) {
                          updateEdit(t.tenant_id, "pro_rata_share", update.pro_rata_share);
                        }
                      }}
                    />
                  </div>
                  <div className="grid gap-5 lg:grid-cols-3">
                    {/* CAM Recovery */}
                    <div className="bg-white rounded-lg border border-grove-200 p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500 mb-3">
                        CAM Recovery
                      </h4>
                      <div className="space-y-3">
                        <Field label="Method">
                          <select
                            value={cam.cam_type || "gross"}
                            onChange={(e) =>
                              setCamMethod(t.tenant_id, e.target.value)
                            }
                            className="input-sm"
                          >
                            {CAM_METHODS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                        </Field>

                        {(cam.cam_type === "base_year_stop" ||
                          cam.cam_type === "expense_cap") && (
                          <>
                            <Field label="Base Year">
                              <input
                                type="number"
                                value={cam.base_year ?? ""}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "base_year",
                                    e.target.value
                                      ? parseInt(e.target.value)
                                      : null
                                  )
                                }
                                placeholder="e.g. 2024"
                                className="input-sm"
                              />
                            </Field>
                            <Field label="Base Amount ($)">
                              <input
                                type="number"
                                value={cam.base_year_amount ?? ""}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "base_year_amount",
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : null
                                  )
                                }
                                placeholder="Override actual"
                                className="input-sm"
                              />
                            </Field>
                            <Field label="Stop Escalation (%)">
                              <PctInput
                                value={cam.stop_escalation_pct}
                                onChange={(v) =>
                                  updateEdit(t.tenant_id, "stop_escalation_pct", v)
                                }
                                placeholder="0 = fixed stop"
                              />
                            </Field>
                            {(cam as any)._stop_derivation && (
                              <div className="col-span-full bg-grove-50 border border-grove-200 rounded-lg p-3 text-[11px] text-bark-500">
                                <p className="font-semibold text-bark-600 mb-1">Stop Derivation (from CAM Rec)</p>
                                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                                  <span>Stop PSF:</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap">{fmtPSF((cam as any)._stop_derivation.stop_psf)}</span>
                                  <span>Property GLA:</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap">{fmtSF((cam as any)._stop_derivation.property_gla || 0)}</span>
                                  <span>Stop Total (PSF x GLA):</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap">{fmtCurrency((cam as any)._stop_derivation.stop_total || 0)}</span>
                                  <span>Total Expenses:</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap">{fmtCurrency((cam as any)._stop_derivation.total_expenses || 0)}</span>
                                  <span className="border-t border-grove-200 pt-1">CAM Pool Base:</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap border-t border-grove-200 pt-1">{fmtCurrency((cam as any)._stop_derivation.cam_base_year_amount || 0)}</span>
                                  <span>Tax Pool Base:</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap">{fmtCurrency((cam as any)._stop_derivation.tax_base_year_amount || 0)}</span>
                                  <span>Insurance Pool Base:</span>
                                  <span className="text-right font-medium tabular-nums whitespace-nowrap">{fmtCurrency((cam as any)._stop_derivation.insurance_base_year_amount || 0)}</span>
                                </div>
                              </div>
                            )}
                          </>
                        )}

                        {cam.cam_type === "expense_stop" && (
                          <>
                            <Field label="Expense Stop ($/SF)">
                              <input
                                type="number"
                                step="0.25"
                                value={cam.expense_stop_psf ?? ""}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "expense_stop_psf",
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : null
                                  )
                                }
                                placeholder="e.g. 8.50"
                                className="input-sm"
                              />
                            </Field>
                            <Field label="Stop Escalation (%)">
                              <PctInput
                                value={cam.stop_escalation_pct}
                                onChange={(v) =>
                                  updateEdit(t.tenant_id, "stop_escalation_pct", v)
                                }
                                placeholder="0 = fixed stop"
                              />
                            </Field>
                            <p className="text-[11px] text-bark-400">
                              Tenant reimburses pro-rata expenses above the
                              stop. Fixed $/SF from the lease — not tied to a
                              base year.
                            </p>
                          </>
                        )}

                        {cam.cam_type === "expense_cap" && (
                          <>
                            <Field label="Annual Ceiling (%)">
                              <PctInput
                                value={cam.expense_cap_pct}
                                onChange={(v) =>
                                  updateEdit(t.tenant_id, "expense_cap_pct", v)
                                }
                                placeholder="e.g. 5.0"
                              />
                            </Field>
                            <label className="flex items-center gap-2 text-xs text-bark-600">
                              <input
                                type="checkbox"
                                checked={cam.expense_cap_compounding ?? true}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "expense_cap_compounding",
                                    e.target.checked
                                  )
                                }
                                className="rounded border-grove-300"
                              />
                              Compounding
                            </label>
                            <div>
                              <Field label="Annual Floor (%)">
                                <PctInput
                                  value={cam.cap_floor_pct}
                                  onChange={(v) =>
                                    updateEdit(t.tenant_id, "cap_floor_pct", v)
                                  }
                                  placeholder="Min increase"
                                />
                              </Field>
                              <p className="text-[10px] text-bark-300 mt-0.5">
                                Minimum annual CAM increase (enter 2 for 2%)
                              </p>
                            </div>
                            <Field label="Cap Basis">
                              <select
                                value={cam.expense_cap_basis ?? "fixed"}
                                onChange={(e) => updateEdit(t.tenant_id, "expense_cap_basis", e.target.value)}
                                className="w-full py-1 text-xs border border-grove-300 rounded bg-grove-50 text-bark-700 pl-1.5"
                              >
                                <option value="fixed">Fixed %</option>
                                <option value="cpi_plus">CPI + Spread</option>
                              </select>
                            </Field>
                            {cam.expense_cap_basis === "cpi_plus" && (
                              <Field label="CPI Spread (%)">
                                <PctInput
                                  value={cam.cap_cpi_spread ?? 0}
                                  onChange={(v) => updateEdit(t.tenant_id, "cap_cpi_spread", v)}
                                  placeholder="e.g. 1.0"
                                />
                              </Field>
                            )}
                          </>
                        )}

                        {cam.cam_type === "fixed_psf" && (
                          <>
                            <Field label="Fixed $/SF">
                              <input
                                type="number"
                                step="0.01"
                                value={cam.fixed_cam_psf ?? ""}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "fixed_cam_psf",
                                    e.target.value
                                      ? parseFloat(e.target.value)
                                      : null
                                  )
                                }
                                placeholder="e.g. 8.50"
                                className="input-sm"
                              />
                            </Field>
                            <Field label="Annual Escalation (%)">
                              <PctInput
                                value={cam.fixed_cam_psf_escalation_pct}
                                onChange={(v) =>
                                  updateEdit(t.tenant_id, "fixed_cam_psf_escalation_pct", v)
                                }
                                placeholder="e.g. 3.0"
                              />
                            </Field>
                          </>
                        )}

                        {cam.cam_type !== "gross" && (
                          <>
                            <Field label="Admin Fee (%)">
                              <PctInput
                                value={cam.management_fee_pct}
                                onChange={(v) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "management_fee_pct",
                                    v ?? 0
                                  )
                                }
                                placeholder="e.g. 15.0"
                              />
                            </Field>
                            <label className="flex items-center gap-2 text-xs text-bark-600">
                              <input
                                type="checkbox"
                                checked={cam.management_fee_included ?? false}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "management_fee_included",
                                    e.target.checked
                                  )
                                }
                                className="rounded border-grove-300"
                              />
                              Include admin fee in pool
                            </label>
                            <Field label="Pool Type">
                              <select
                                value={cam.cam_pool_type || "full"}
                                onChange={(e) =>
                                  updateEdit(
                                    t.tenant_id,
                                    "cam_pool_type",
                                    e.target.value
                                  )
                                }
                                className="input-sm"
                              >
                                <option value="full">
                                  Full (all reimbursable)
                                </option>
                                <option value="controllable_only">
                                  Controllable Only
                                </option>
                              </select>
                            </Field>
                            <div>
                              <span className="text-[11px] font-medium text-bark-500 block mb-1">
                                Exclusions
                              </span>
                              <div className="flex flex-wrap gap-x-3 gap-y-1">
                                {EXCLUSION_OPTIONS.map((ex) => (
                                  <label
                                    key={ex.value}
                                    className="flex items-center gap-1.5 text-[11px] text-bark-600"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        cam.cam_exclusions?.includes(
                                          ex.value
                                        ) ?? false
                                      }
                                      onChange={(e) => {
                                        const current =
                                          cam.cam_exclusions || [];
                                        const next = e.target.checked
                                          ? [...current, ex.value]
                                          : current.filter(
                                              (v) => v !== ex.value
                                            );
                                        updateEdit(
                                          t.tenant_id,
                                          "cam_exclusions",
                                          next
                                        );
                                      }}
                                      className="rounded border-grove-300"
                                    />
                                    {ex.label}
                                  </label>
                                ))}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Tax Recovery */}
                    <div className="bg-white rounded-lg border border-grove-200 p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500 mb-3">
                        Tax Recovery
                      </h4>
                      <div className="space-y-3">
                        <Field label="Method">
                          <select
                            value={
                              cam.tax_reimbursement === false
                                ? "none"
                                : cam.tax_reimbursement_type || "NNN"
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              updateEdit(
                                t.tenant_id,
                                "tax_reimbursement",
                                v !== "none"
                              );
                              updateEdit(
                                t.tenant_id,
                                "tax_reimbursement_type",
                                v
                              );
                            }}
                            className="input-sm"
                          >
                            {TAX_INS_METHODS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                        {cam.tax_reimbursement !== false &&
                          (cam.tax_reimbursement_type === "base_year_stop" || cam.tax_reimbursement_type === "expense_cap") && (
                            <>
                              <Field label="Base Year">
                                <input
                                  type="number"
                                  value={cam.tax_base_year ?? ""}
                                  onChange={(e) =>
                                    updateEdit(
                                      t.tenant_id,
                                      "tax_base_year",
                                      e.target.value
                                        ? parseInt(e.target.value)
                                        : null
                                    )
                                  }
                                  placeholder="e.g. 2024"
                                  className="input-sm"
                                />
                              </Field>
                              <Field label="Base Amount ($)">
                                <input
                                  type="number"
                                  value={cam.tax_base_year_amount ?? ""}
                                  onChange={(e) =>
                                    updateEdit(
                                      t.tenant_id,
                                      "tax_base_year_amount",
                                      e.target.value
                                        ? parseFloat(e.target.value)
                                        : null
                                    )
                                  }
                                  placeholder="Override actual"
                                  className="input-sm"
                                />
                              </Field>
                              <Field label="Stop Escalation (%)">
                                <PctInput
                                  value={cam.tax_stop_escalation_pct}
                                  onChange={(v) =>
                                    updateEdit(t.tenant_id, "tax_stop_escalation_pct", v)
                                  }
                                  placeholder="0 = fixed"
                                />
                              </Field>
                            </>
                          )}
                        {cam.tax_reimbursement !== false &&
                          cam.tax_reimbursement_type === "expense_cap" && (
                            <>
                              <Field label="Cap (%)">
                                <PctInput
                                  value={cam.tax_expense_cap_pct}
                                  onChange={(v) =>
                                    updateEdit(t.tenant_id, "tax_expense_cap_pct", v)
                                  }
                                  placeholder="e.g. 5.0"
                                />
                              </Field>
                              <label className="flex items-center gap-2 text-xs text-bark-600">
                                <input
                                  type="checkbox"
                                  checked={cam.tax_expense_cap_compounding ?? true}
                                  onChange={(e) =>
                                    updateEdit(
                                      t.tenant_id,
                                      "tax_expense_cap_compounding",
                                      e.target.checked
                                    )
                                  }
                                  className="rounded border-grove-300"
                                />
                                Compounding
                              </label>
                            </>
                          )}
                        {cam.tax_reimbursement !== false &&
                          cam.tax_reimbursement_type !== "none" && (
                            <Field label="Admin Fee (%)">
                              <PctInput
                                value={cam.tax_admin_fee_pct}
                                onChange={(v) =>
                                  updateEdit(t.tenant_id, "tax_admin_fee_pct", v ?? 0)
                                }
                                placeholder="e.g. 5.0"
                              />
                            </Field>
                          )}
                      </div>
                    </div>

                    {/* Insurance Recovery */}
                    <div className="bg-white rounded-lg border border-grove-200 p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500 mb-3">
                        Insurance Recovery
                      </h4>
                      <div className="space-y-3">
                        <Field label="Method">
                          <select
                            value={
                              cam.insurance_reimbursement === false
                                ? "none"
                                : cam.insurance_reimbursement_type || "NNN"
                            }
                            onChange={(e) => {
                              const v = e.target.value;
                              updateEdit(
                                t.tenant_id,
                                "insurance_reimbursement",
                                v !== "none"
                              );
                              updateEdit(
                                t.tenant_id,
                                "insurance_reimbursement_type",
                                v
                              );
                            }}
                            className="input-sm"
                          >
                            {TAX_INS_METHODS.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                        {cam.insurance_reimbursement !== false &&
                          (cam.insurance_reimbursement_type === "base_year_stop" || cam.insurance_reimbursement_type === "expense_cap") && (
                            <>
                              <Field label="Base Year">
                                <input
                                  type="number"
                                  value={cam.insurance_base_year ?? ""}
                                  onChange={(e) =>
                                    updateEdit(
                                      t.tenant_id,
                                      "insurance_base_year",
                                      e.target.value
                                        ? parseInt(e.target.value)
                                        : null
                                    )
                                  }
                                  placeholder="e.g. 2024"
                                  className="input-sm"
                                />
                              </Field>
                              <Field label="Base Amount ($)">
                                <input
                                  type="number"
                                  value={cam.insurance_base_year_amount ?? ""}
                                  onChange={(e) =>
                                    updateEdit(
                                      t.tenant_id,
                                      "insurance_base_year_amount",
                                      e.target.value
                                        ? parseFloat(e.target.value)
                                        : null
                                    )
                                  }
                                  placeholder="Override actual"
                                  className="input-sm"
                                />
                              </Field>
                              <Field label="Stop Escalation (%)">
                                <PctInput
                                  value={cam.insurance_stop_escalation_pct}
                                  onChange={(v) =>
                                    updateEdit(t.tenant_id, "insurance_stop_escalation_pct", v)
                                  }
                                  placeholder="0 = fixed"
                                />
                              </Field>
                            </>
                          )}
                        {cam.insurance_reimbursement !== false &&
                          cam.insurance_reimbursement_type === "expense_cap" && (
                            <>
                              <Field label="Cap (%)">
                                <PctInput
                                  value={cam.insurance_expense_cap_pct}
                                  onChange={(v) =>
                                    updateEdit(t.tenant_id, "insurance_expense_cap_pct", v)
                                  }
                                  placeholder="e.g. 5.0"
                                />
                              </Field>
                              <label className="flex items-center gap-2 text-xs text-bark-600">
                                <input
                                  type="checkbox"
                                  checked={cam.insurance_expense_cap_compounding ?? true}
                                  onChange={(e) =>
                                    updateEdit(
                                      t.tenant_id,
                                      "insurance_expense_cap_compounding",
                                      e.target.checked
                                    )
                                  }
                                  className="rounded border-grove-300"
                                />
                                Compounding
                              </label>
                            </>
                          )}
                        {cam.insurance_reimbursement !== false &&
                          cam.insurance_reimbursement_type !== "none" && (
                            <Field label="Admin Fee (%)">
                              <PctInput
                                value={cam.insurance_admin_fee_pct}
                                onChange={(v) =>
                                  updateEdit(t.tenant_id, "insurance_admin_fee_pct", v ?? 0)
                                }
                                placeholder="e.g. 5.0"
                              />
                            </Field>
                          )}
                      </div>
                    </div>
                  </div>

                  {/* Pro-Rata Share */}
                  {cam.cam_type !== "gross" && (
                    <div className="mt-4 bg-white rounded-lg border border-grove-200 p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500 mb-3">
                        Pro-Rata Share
                      </h4>
                      <div className="flex items-center gap-4">
                        <div className="text-xs text-bark-400">
                          Computed: {totalLeasableArea > 0
                            ? fmtPercent(t.leased_sf / totalLeasableArea)
                            : "N/A"}{" "}
                          ({fmtSF(t.leased_sf)} / {fmtSF(totalLeasableArea)})
                        </div>
                        <Field label="Override (%)">
                          <PctInput
                            value={(() => {
                              const editData = edits[t.tenant_id as keyof typeof edits] as
                                | { pro_rata_share?: number | null }
                                | undefined;
                              if (editData?.pro_rata_share !== undefined) return editData.pro_rata_share;
                              return t.pro_rata_share ?? null;
                            })()}
                            onChange={(v) => updateEdit(t.tenant_id, "pro_rata_share", v)}
                            placeholder="Use computed"
                          />
                        </Field>
                      </div>
                    </div>
                  )}

                  {/* Per-Category Caps */}
                  {cam.cam_type === "expense_cap" && (
                    <CategoryCapsSection
                      cam={cam}
                      tenantId={t.tenant_id}
                      updateEdit={updateEdit}
                      edits={edits}
                    />
                  )}

                  {/* Per-Tenant Custom Pool Configuration */}
                  {cam.cam_type !== "gross" && propertyPools.length > 0 && (
                    <div className="mt-4 bg-white rounded-lg border border-grove-200 p-4">
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-bark-500 mb-3">
                        Custom Expense Pool Reimbursement
                      </h4>
                      <div className="space-y-3">
                        {propertyPools.map((pool) => {
                          const editData = edits[t.tenant_id as keyof typeof edits] as
                            | { pool_reimbursements?: PoolReimbursement[] }
                            | undefined;
                          const poolReimbs = editData?.pool_reimbursements ?? t.pool_reimbursements ?? [];
                          const poolConfig = poolReimbs.find((pr) => pr.pool_id === pool.pool_id);
                          const currentType = poolConfig?.reimbursement_type ?? "NNN";

                          const updatePoolConfig = (field: string, val: unknown) => {
                            const existing = [...poolReimbs];
                            const idx = existing.findIndex((pr) => pr.pool_id === pool.pool_id);
                            if (idx >= 0) {
                              existing[idx] = { ...existing[idx], [field]: val };
                            } else {
                              existing.push({
                                pool_id: pool.pool_id,
                                reimbursement_type: "NNN",
                                [field]: val,
                              });
                            }
                            updateEdit(t.tenant_id, "pool_reimbursements", existing);
                          };

                          return (
                            <div key={pool.pool_id} className="border border-grove-100 rounded-lg p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-bark-600">
                                  {pool.pool_name}
                                </span>
                                <span className="text-[10px] text-bark-300">
                                  {pool.source_categories.map((s) =>
                                    s.startsWith("other:") ? s.slice(6) : prettyFieldLabel(s)
                                  ).join(", ")}
                                </span>
                              </div>
                              <div className="grid gap-3 sm:grid-cols-3">
                                <Field label="Method">
                                  <select
                                    value={currentType}
                                    onChange={(e) => updatePoolConfig("reimbursement_type", e.target.value)}
                                    className="input-sm"
                                  >
                                    <option value="NNN">NNN (Pro-Rata)</option>
                                    <option value="base_year_stop">Base Year Stop</option>
                                    <option value="expense_cap">Expense Cap</option>
                                    <option value="none">None</option>
                                  </select>
                                </Field>
                                {(currentType === "base_year_stop" || currentType === "expense_cap") && (
                                  <>
                                    <Field label="Base Year">
                                      <input
                                        type="number"
                                        value={poolConfig?.base_year ?? ""}
                                        onChange={(e) => updatePoolConfig("base_year", e.target.value ? parseInt(e.target.value) : null)}
                                        placeholder="e.g. 2024"
                                        className="input-sm"
                                      />
                                    </Field>
                                    <Field label="Base Amount ($)">
                                      <input
                                        type="number"
                                        value={poolConfig?.base_year_amount ?? ""}
                                        onChange={(e) => updatePoolConfig("base_year_amount", e.target.value ? parseFloat(e.target.value) : null)}
                                        placeholder="Override"
                                        className="input-sm"
                                      />
                                    </Field>
                                  </>
                                )}
                                {currentType === "expense_cap" && (
                                  <Field label="Cap (%)">
                                    <PctInput
                                      value={poolConfig?.expense_cap_pct ?? null}
                                      onChange={(v) => updatePoolConfig("expense_cap_pct", v)}
                                      placeholder="e.g. 5.0"
                                    />
                                  </Field>
                                )}
                                {currentType !== "none" && (
                                  <Field label="Admin Fee (%)">
                                    <PctInput
                                      value={poolConfig?.admin_fee_pct ?? null}
                                      onChange={(v) => updatePoolConfig("admin_fee_pct", v ?? 0)}
                                      placeholder="e.g. 5.0"
                                    />
                                  </Field>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Save button */}
                  {isDirty && !isSaved && (
                    <div className="mt-4 flex justify-end">
                      <button
                        onClick={() => saveTenant(t.tenant_id)}
                        disabled={isSaving}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-forest-700 rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-colors"
                      >
                        <Save className="w-3.5 h-3.5" />
                        {isSaving ? "Saving\u2026" : "Save"}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
